import { Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import type { BlockedNextDoc } from './documentTypeCatalog';

// Placeholders for the part of a category's document catalog that hasn't been
// generated yet — closing out a role's document list in either layout.

/** Muted, italic explanation shown in place of an assistant's document list
 * when nothing has been generated yet *and* nothing in its category can be
 * generated yet either — names the prerequisite artifact(s) still missing. */
export function BlockedCategoryNotice({ blocked, sx }: { blocked: BlockedNextDoc; sx?: SxProps<Theme> }) {
  const depNames = blocked.missingDeps.map((d) => d.name).join(', ');
  return (
    <Typography variant="caption" color="text.disabled" sx={[{ fontStyle: 'italic' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      Locked — generate {depNames} first to unlock "{blocked.docType.name}"
    </Typography>
  );
}

/**
 * Role-tinted placeholder card closing out a category's document grid — how
 * many more of that category's document types could still be generated. Sized
 * and bordered like the document cards beside it so the count sits in the same
 * line as them instead of being stranded in the panel's leftover space.
 * Kept as a standalone count (not tied to the Next Steps/blocked messaging)
 * so the total addressable catalog stays visible.
 */
export function RemainingDocTypesCard({ count, roleColor }: { count: number; roleColor: string }) {
  return (
    <GlassPanel
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
        p: 1.5,
        border: '1px dashed',
        borderColor: alpha(roleColor, 0.5),
        backgroundColor: alpha(roleColor, 0.06),
      }}
    >
      <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2, color: roleColor }}>
        +{count}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
        more document type{count === 1 ? '' : 's'} available
      </Typography>
    </GlassPanel>
  );
}

/**
 * Same count as {@link RemainingDocTypesCard}, trimmed to a single row for the
 * column layout's "Linked Artifacts" list.
 */
export function RemainingDocTypesRow({ count, roleColor }: { count: number; roleColor: string }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 'var(--radius-sm)',
        border: '1px dashed',
        borderColor: alpha(roleColor, 0.5),
        backgroundColor: alpha(roleColor, 0.06),
      }}
    >
      <Typography variant="caption" fontWeight={700} sx={{ color: roleColor, minWidth: 0 }} noWrap>
        +{count} more document type{count === 1 ? '' : 's'} available
      </Typography>
    </Stack>
  );
}
